import { useState, useEffect } from 'react';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  Legend
} from 'recharts';
import { TimeRange, getItemAdditionStats, ItemAdditionDataPoint } from '@/services/analyticsService';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CalendarClock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useIsMobile } from '@/hooks/use-mobile';

interface ItemAdditionsChartProps {
  timeRange?: TimeRange;
  standalone?: boolean;
}

const ItemAdditionsChart = ({ 
  timeRange: externalTimeRange,
  standalone = false
}: ItemAdditionsChartProps) => {
  const [timeRange, setTimeRange] = useState<TimeRange>(externalTimeRange || '7d');
  const [data, setData] = useState<ItemAdditionDataPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (externalTimeRange) {
      setTimeRange(externalTimeRange);
    }
  }, [externalTimeRange]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const stats = await getItemAdditionStats(timeRange);
        setData(stats);
      } catch (error) {
        console.error('Error fetching item addition stats:', error);
        toast.error('Failed to load item addition statistics');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [timeRange]);

  const handleTimeRangeChange = (range: TimeRange) => {
    setTimeRange(range);
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return `${date.getMonth() + 1}/${date.getDate()}`;
  };

  const renderChart = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-full">
          <Loader2 className="h-6 w-6 sm:h-8 sm:w-8 animate-spin text-primary" />
        </div>
      );
    }
    
    if (data.length === 0) {
      return (
        <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
          No data available for the selected time range
        </div>
      );
    }
    
    return (
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={data}
          margin={{ 
            top: 5,
            right: isMobile ? 10 : 30, 
            left: isMobile ? 0 : 20, 
            bottom: 5 
          }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
          <XAxis 
            dataKey="date" 
            tickFormatter={formatDate}
            tick={{ fontSize: isMobile ? 10 : 12 }}
            tickMargin={isMobile ? 5 : 10}
            interval={isMobile ? 'preserveStartEnd' : 0}
          />
          <YAxis 
            allowDecimals={false}
            tick={{ fontSize: isMobile ? 10 : 12 }}
            tickMargin={isMobile ? 5 : 10}
            width={isMobile ? 30 : 40}
          />
          <Tooltip
            labelFormatter={(label) => `Date: ${new Date(label).toLocaleDateString()}`}
            formatter={(value) => [`${value} items`, 'Added']}
            contentStyle={{ 
              backgroundColor: 'var(--background)', 
              borderColor: 'var(--border)',
              borderRadius: '0.375rem',
              fontSize: isMobile ? '0.75rem' : '0.875rem',
              padding: isMobile ? '8px' : '10px'
            }}
          />
          <Legend 
            wrapperStyle={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}
            verticalAlign={isMobile ? 'top' : 'bottom'}
            height={isMobile ? 15 : 25}
          />
          <Line
            type="monotone"
            dataKey="count"
            name="Items Added"
            stroke="#8884d8"
            strokeWidth={isMobile ? 1.5 : 2}
            activeDot={{ r: isMobile ? 6 : 8 }}
            dot={{ r: isMobile ? 3 : 4 }}
          />
        </LineChart>
      </ResponsiveContainer>
    );
  };

  if (standalone) {
    return (
      <Card className="bg-background border border-border/50 shadow-subtle">
        <CardHeader className="p-3 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                <CalendarClock className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                Item Additions Over Time
              </CardTitle>
              <CardDescription className="text-xs sm:text-sm">
                Track how many items were added over time
              </CardDescription>
            </div>
            <div className="flex gap-1 sm:gap-2">
              <Button 
                variant={timeRange === '1d' ? 'default' : 'outline'} 
                size="sm"
                className="text-xs h-7 px-2 sm:px-3"
                onClick={() => handleTimeRangeChange('1d')}
              >
                1d
              </Button>
              <Button 
                variant={timeRange === '7d' ? 'default' : 'outline'} 
                size="sm"
                className="text-xs h-7 px-2 sm:px-3"
                onClick={() => handleTimeRangeChange('7d')}
              >
                7d
              </Button>
              <Button 
                variant={timeRange === '30d' ? 'default' : 'outline'} 
                size="sm"
                className="text-xs h-7 px-2 sm:px-3"
                onClick={() => handleTimeRangeChange('30d')}
              >
                30d
              </Button>
              <Button 
                variant={timeRange === 'all' ? 'default' : 'outline'} 
                size="sm"
                className="text-xs h-7 px-2 sm:px-3"
                onClick={() => handleTimeRangeChange('all')}
              >
                All
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-3 sm:p-6 pt-0">
          <div className="h-[200px] sm:h-[300px] w-full">
            {renderChart()}
          </div>
        </CardContent>
      </Card>
    );
  }

  return renderChart();
};

export default ItemAdditionsChart;
